import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../../Components/UI/button";
import { Label } from "../../../Components/UI/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog";
import { X, Check, Loader2, ArrowLeft, Smartphone, RefreshCw } from "lucide-react";
import { setGlobalLoading } from "../../../Store/Actions";
import { generateWDQR, checkStatusWD } from "./WDConfigAPI";
import { toast } from "sonner";
import { getUserConfig } from "../../../Store/Reducers";
import { getConfigData } from "../../../Utils/Storage";

export default function WhatsAppIntgnConfig({ onClose, onForceClose }) {
  const dispatch = useDispatch();
  const [isGenerating, setIsGenerating] = useState(false);
  const [wspProvider, setWspProvider] = useState("");
  const [qrCode, setQrCode] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [storedConfig, setStoredConfig] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [instanceID, setInstanceID] = useState("");
  const isMounted = useRef(true);

  const POLL_INTERVAL = 5000;
  const POLL_TIMEOUT = 20000;

  useEffect(() => {
	fetchConfigAndGenerateQR();
	return () => {
		isMounted.current = false;
	};
  }, []);

  const fetchConfigAndGenerateQR = async () => {
    try {
      dispatch(setGlobalLoading(true));

      const record = getConfigData();

      let configsData = null;
      let preferredWDProvider = "";

      if (record) {
        if (record?.WDPreferred) {
          preferredWDProvider = record.WDPreferred;
          setWspProvider(preferredWDProvider);
        }

        if (record?.WDConfig) {
          const configsArr = JSON.parse(record.WDConfig);
          const cfg = configsArr?.[0];

          configsData = {
            WDProvider: cfg?.WDProvider ?? null,
            WDToken: cfg?.WDToken ? JSON.stringify(cfg.WDToken) : null
          };
        }
      }

      if (configsData) {
        if (configsData.WDToken) setStoredConfig(configsData.WDToken);
      }

      await generateQRCode(preferredWDProvider, configsData?.WDToken || "");
    } catch (error) {
      toast.error("Failed to load configuration.");
      dispatch(setGlobalLoading(false));
    }
  };

  const generateQRCode = async (provider, configString) => {
    try {
      setIsGenerating(true);
      setQrCode(null);

      let configPayload = [];
      if (configString) {
        try {
          configPayload = JSON.parse(configString);
        } catch (e) {
          console.error("Config is not valid JSON", e);
          toast.error("Invalid configuration format.");
          dispatch(setGlobalLoading(false));
          return;
        }
      }

      const response = await generateWDQR("WD", provider, configPayload);

	    if(response.error){
			console.error(response.error);
	    }
        const updatePackage = JSON.parse(response.updatePackage);

        setQrCode(updatePackage.Table[0].RetData);
	    // handlePolling();

		setIsButtonDisabled(true);
		setTimeout(() => { setIsButtonDisabled(false) }, POLL_TIMEOUT);

    } catch (error) {
      toast.error(error.message || "Failed to generate QR code.");
	  onForceClose();
    } finally {
      setIsGenerating(false);
      dispatch(setGlobalLoading(false));
    }
  };

    const pollAttemptRef = useRef(0);

    const checkWDStatus = async () => {
		try {
			const rawConfig = getUserConfig();

			if (!rawConfig) return null;

			let isSuccess = false;

			const configsArr = JSON.parse(rawConfig.WDConfig);
			const cfg = configsArr?.[0];

			const WDProvider = cfg?.P;
			const WDToken = cfg?.C;

			if (!WDProvider || !WDToken) {
				console.warn("Invalid WD config");
				return null;
			}

			const response = await checkStatusWD("WD", WDProvider, [WDToken]);
			
			const updatePackage = JSON.parse(response?.updatePackage ?? "{}");
			const retDataStr = updatePackage?.Table?.[0]?.RetData ?? "{}";
			const retData = JSON.parse(retDataStr);
			const status = retData?.data?.status;

			if (status === "success") {
				isSuccess = true;
				setIsConfigured(true);
			}

			return isSuccess;
			} 
		catch (err) {
				console.error("checkWDStatus failed", err);
				return null;
			}
	};

	const handlePolling = async () => {
		if (!isMounted.current) return;

		const maxDuration = POLL_TIMEOUT; // 20 sec
		const interval = POLL_INTERVAL;   // 1 sec
		const startTime = Date.now();

		const poll = async () => {
			try {
				pollAttemptRef.current += 1;

				const status = await checkWDStatus();

				if (status) {
					onClose();
					setIsConfigured(true);
					return;
				}

				if (Date.now() - startTime < maxDuration) {
					setTimeout(poll, interval);
				} else {
					console.warn("Polling timeout reached");
				}
			} catch (err) {
				console.error("Polling error", err);
			}
		};

		poll();
	};

	const handleCheckStatusClick = async () => {
		const status = await checkWDStatus();
		if (status) {
			onClose();
		}
	};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[540px] w-full p-0 flex flex-col gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-4 flex flex-row items-center justify-between space-y-0 border-b">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-lg font-semibold">
              WhatsApp Integration
            </DialogTitle>
          </div>
          <DialogClose onClick={onForceClose} className="p-1 hover:bg-gray-100 rounded-md">
            <X className="h-5 w-5" />
          </DialogClose> 
        </DialogHeader>

        {/* Content */}
        <div className="p-4 space-y-2 flex-1 overflow-y-auto">
          {/* Instructions */}
          {(qrCode || isGenerating) && (
            <div className="bg-white flex flex-col border border-sahi-light-grey/30 rounded-lg p-4 gap-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <Smartphone className="w-5 h-5 text-text-color" />
                </div>
                <div className="flex-1 space-y-2">
                  <h4 className="font-semibold text-text-color text-sm">
                    Link Your WhatsApp Device
                  </h4>
                  <ol className="text-xs text-sub-text-color space-y-1 list-decimal list-inside">
                    <li>Open WhatsApp on your phone</li>
                    <li>Go to <span className="font-medium">Settings</span> → <span className="font-medium">Linked Devices</span></li>
                    <li>Tap on <span className="font-medium">Link a Device</span></li>
                    <li>Scan the QR code below</li>
					<li>Click on Check button, once connected</li>
                  </ol>
                </div>
              </div>

              {qrCode && !isGenerating && (
                <div className="flex flex-col items-center justify-center p-4 pb-0 border-t-2 border-sahi-light-grey/50">
                  <div className="relative">
                    <img 
                      src={qrCode} 
                      alt="WhatsApp QR Code" 
                      className="w-48 h-48 object-contain"
                    />
                  </div>
                  {!isConfigured && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Waiting for connection</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {qrCode && (
          <div className="p-4 sm:sticky sm:bottom-0 sm:bg-background sm:pb-3">
            <div className="flex gap-4 sm:flex-row sm:justify-start sm:gap-3 w-full">
              <Button
                onClick={handleCheckStatusClick}
                disabled={isButtonDisabled}
                className="sahi-primary-button"
              >
                <Check className="w-4 h-4" />
                Check
              </Button>
              <Button
                variant="secondary"
                onClick={onClose}
                className="sahi-secondary-button"
              >
                <X className="w-3 h-3" />
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}